import {
  computeAssessmentVsModificationDelta,
  findLeaseByName,
  nextMonthLabel,
  type JournalEntryResult,
} from "./leaseReassessmentJournalEntry";

export interface ModificationJournalFlags {
  scope_reduced: "Yes" | "No" | "";
  standalone_selling_price: "Yes" | "No" | "";
  lease_period_increased: "Yes" | "No" | "";
  other_reason: string;
  original_number_of_assets: number;
  revised_number_of_assets: number;
}

export type ModificationJournalResult =
  | { ok: false; message: string }
  | {
      ok: true;
      journalCase: 1;
      eventMonth: string;
      priorMonth: string;
      drLeaseLiability: number;
      drProfitAndLoss: number;
      crRightOfUseAsset: number;
      crProfitAndLoss: number;
      delta: number;
      rouNextMonthLabel: string;
      rouAssessmentForNextMonth: number;
    }
  | {
      ok: true;
      journalCase: 2 | 3;
      eventMonth: string;
      priorMonth: string;
      closingPriorAssessment: number;
      openingEvent: number;
      delta: number;
      amount: number;
    };

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return NaN;
}

/** User Case 1: reduction in scope. Case 2 / 3: same journal format (reassessment-style). */
export function journalCaseFromFlags(flags: ModificationJournalFlags): 1 | 2 | 3 | null {
  if (flags.scope_reduced === "Yes") return 1;
  if (flags.standalone_selling_price === "No" && flags.lease_period_increased === "Yes") return 3;
  if (
    flags.other_reason.trim().length > 0 ||
    (flags.standalone_selling_price === "No" && flags.lease_period_increased === "No")
  ) {
    return 2;
  }
  return null;
}

export function computeModificationJournalEntry(
  leaseName: string,
  flags: ModificationJournalFlags,
  assessmentData: unknown,
  modificationOutputData: unknown,
): ModificationJournalResult {
  const jc = journalCaseFromFlags(flags);
  if (jc === null) {
    return {
      ok: false,
      message:
        "Cannot determine journal case: set scope reduction, or standalone selling price / lease period / other reason per your modification scenario.",
    };
  }

  if (jc === 2 || jc === 3) {
    const d = computeAssessmentVsModificationDelta(leaseName, assessmentData, modificationOutputData);
    if (!d.ok) return d;
    return {
      ok: true,
      journalCase: jc,
      eventMonth: d.eventMonth,
      priorMonth: d.priorMonth,
      closingPriorAssessment: d.closingPriorAssessment,
      openingEvent: d.openingEvent,
      delta: d.delta,
      amount: d.amount,
    };
  }

  const orig = Math.max(0, Math.round(Number(flags.original_number_of_assets) || 0));
  const rev = Math.max(0, Math.round(Number(flags.revised_number_of_assets) || 0));
  if (orig <= 0) {
    return {
      ok: false,
      message: "Original Number of Assets must be greater than zero for a scope-reduction journal entry.",
    };
  }

  const d: JournalEntryResult = computeAssessmentVsModificationDelta(
    leaseName,
    assessmentData,
    modificationOutputData,
  );
  if (!d.ok) return d;

  const modLease = findLeaseByName((modificationOutputData as Record<string, unknown>)?.leases, leaseName);
  const sch = modLease?.schedule;
  const firstRow = Array.isArray(sch) && sch.length > 0 ? (sch[0] as Record<string, unknown>) : null;
  const evtMonth = String(firstRow?.["Month"] ?? "").trim();
  if (!evtMonth) {
    return { ok: false, message: "Could not read modification event month." };
  }

  const nextM = nextMonthLabel(evtMonth);
  if (!nextM) {
    return { ok: false, message: `Could not compute month after "${evtMonth}".` };
  }

  const aLease = findLeaseByName((assessmentData as Record<string, unknown>)?.leases, leaseName);
  const aSchedule = aLease?.schedule;
  let rouNext = NaN;
  if (Array.isArray(aSchedule)) {
    for (const row of aSchedule) {
      if (!row || typeof row !== "object") continue;
      const r = row as Record<string, unknown>;
      if (String(r["Month"] ?? "").trim() === nextM) {
        rouNext = num(r["ROU"]);
        break;
      }
    }
  }

  if (Number.isNaN(rouNext)) {
    return {
      ok: false,
      message: `No ROU in assessment for month "${nextM}" (month after modification event "${evtMonth}").`,
    };
  }

  const drLL = Math.abs(Math.round(d.delta));
  const crRou = Math.round((rouNext * (orig - rev)) / orig);
  const drPL = Math.max(0, crRou - drLL);
  const crPL = Math.max(0, drLL - crRou);

  return {
    ok: true,
    journalCase: 1,
    eventMonth: d.eventMonth,
    priorMonth: d.priorMonth,
    drLeaseLiability: drLL,
    drProfitAndLoss: drPL,
    crRightOfUseAsset: crRou,
    crProfitAndLoss: crPL,
    delta: d.delta,
    rouNextMonthLabel: nextM,
    rouAssessmentForNextMonth: rouNext,
  };
}

/** Signed ROU line only (ignores P&L). Positive = ROU debit / increase, negative = ROU credit / decrease. */
export function signedRouAdjustmentFromModificationJournal(j: ModificationJournalResult): number | null {
  if (!j.ok) return null;
  if (j.journalCase === 1) return -Math.round(j.crRightOfUseAsset);
  return Math.round(j.delta);
}

